import { supabase } from "../lib/supabase";

export interface EssayConversationDB {
  id: string;
  essay_id: string;
  selection_start_index: number | null;
  selection_end_index: number | null;
  essay_snapshot: string;
  intent: string;
  user_message: string;
  assistant_message: string;
  created_at: string;
  updated_at: string;
  // Quality scores (only present for full_review intent)
  structure_score: number | null;
  voice_score: number | null;
  specificity_score: number | null;
  feedback_keywords: string[] | null;
}

export interface EssayConversationMetadataDB {
  essay_id: string;
  conversation_summary: string;
  total_messages: number;
  last_summary_at: string;
  created_at: string;
  updated_at: string;
}

// ==========================================
// ESSAY CONVERSATION OPERATIONS
// ==========================================

export async function saveEssayConversation(
  essayId: string,
  essaySnapshot: string,
  userMessage: string,
  assistantMessage: string,
  intent: string,
  selectionStartIndex: number | null = null,
  selectionEndIndex: number | null = null,
  qualityScores?: {
    structure_score?: number | null;
    voice_score?: number | null;
    specificity_score?: number | null;
    feedback_keywords?: string[] | null;
  }
): Promise<EssayConversationDB> {
  // Check current auth user
  const { data: { user } } = await supabase.auth.getUser();
  console.log("💾 [API] Current authenticated user:", user?.id);

  // Check essay ownership
  const { data: essayData, error: essayError } = await supabase
    .from("essays")
    .select("student_id")
    .eq("id", essayId)
    .single();

  if (essayError) {
    console.error("❌ [API] Error fetching essay:", essayError);
  } else {
    console.log("💾 [API] Essay student_id:", essayData?.student_id);
    console.log("💾 [API] Auth user ID matches essay student_id?", user?.id === essayData?.student_id);
  }

  console.log("💾 [API] saveEssayConversation called with:", {
    essayId,
    essaySnapshotLength: essaySnapshot.length,
    userMessageLength: userMessage.length,
    assistantMessageLength: assistantMessage.length,
    intent,
    selectionStartIndex,
    selectionEndIndex,
    qualityScores,
  });

  const insertData: Record<string, unknown> = {
    essay_id: essayId,
    essay_snapshot: essaySnapshot,
    user_message: userMessage,
    assistant_message: assistantMessage,
    intent,
    selection_start_index: selectionStartIndex,
    selection_end_index: selectionEndIndex,
  };

  // Add quality scores if provided (for full_review intent)
  if (qualityScores) {
    if (qualityScores.structure_score !== undefined) {
      insertData.structure_score = qualityScores.structure_score;
    }
    if (qualityScores.voice_score !== undefined) {
      insertData.voice_score = qualityScores.voice_score;
    }
    if (qualityScores.specificity_score !== undefined) {
      insertData.specificity_score = qualityScores.specificity_score;
    }
    if (qualityScores.feedback_keywords !== undefined) {
      insertData.feedback_keywords = qualityScores.feedback_keywords;
    }
  }

  const { data, error } = await supabase
    .from("essay_conversations")
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error("❌ [API] Error saving essay conversation:", error);
    console.error("❌ [API] Error code:", error.code);
    console.error("❌ [API] Error message:", error.message);
    console.error("❌ [API] Error details:", error.details);
    throw error;
  }

  console.log("✅ [API] Successfully saved essay conversation, ID:", data.id);
  return data;
}

export async function getEssayConversations(
  essayId: string,
  limit = 50
): Promise<EssayConversationDB[]> {
  const { data, error } = await supabase
    .from("essay_conversations")
    .select("*")
    .eq("essay_id", essayId)
    .order("created_at", { ascending: true })
    .limit(limit);

  if (error) {
    console.error("Error fetching essay conversations:", error);
    throw error;
  }

  return data || [];
}

export async function deleteEssayConversation(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from("essay_conversations")
    .delete()
    .eq("id", conversationId);

  if (error) {
    console.error("Error deleting essay conversation:", error);
    throw error;
  }
}

// ==========================================
// ESSAY CONVERSATION METADATA OPERATIONS
// ==========================================

export async function getEssayConversationMetadata(
  essayId: string
): Promise<EssayConversationMetadataDB | null> {
  const { data, error } = await supabase
    .from("essay_conversation_metadata")
    .select("*")
    .eq("essay_id", essayId)
    .single();

  if (error) {
    // If no metadata exists yet, return null (not an error)
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching essay conversation metadata:", error);
    throw error;
  }

  return data;
}

export async function updateEssayConversationMetadata(
  essayId: string,
  conversationSummary: string,
  totalMessages: number
): Promise<EssayConversationMetadataDB> {
  console.log("💾 [API] updateEssayConversationMetadata called with:", {
    essayId,
    conversationSummaryLength: conversationSummary.length,
    totalMessages,
  });

  const { data, error } = await supabase
    .from("essay_conversation_metadata")
    .upsert(
      {
        essay_id: essayId,
        conversation_summary: conversationSummary,
        total_messages: totalMessages,
        last_summary_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "essay_id",
      }
    )
    .select()
    .single();

  if (error) {
    console.error("❌ [API] Error updating essay conversation metadata:", error);
    console.error("❌ [API] Error code:", error.code);
    console.error("❌ [API] Error message:", error.message);
    console.error("❌ [API] Error details:", error.details);
    throw error;
  }

  console.log("✅ [API] Successfully updated essay conversation metadata");
  return data;
}

export async function deleteEssayConversationMetadata(essayId: string): Promise<void> {
  const { error } = await supabase
    .from("essay_conversation_metadata")
    .delete()
    .eq("essay_id", essayId);

  if (error) {
    console.error("Error deleting essay conversation metadata:", error);
    throw error;
  }
}

/**
 * Clear all conversation data for an essay (both conversations and metadata/summary)
 * This provides a "fresh start" for the conversation
 */
export async function clearAllConversations(essayId: string): Promise<void> {
  console.log("🗑️ [API] Clearing all conversations for essay:", essayId);

  // Delete all conversation messages
  const { error: convError } = await supabase
    .from("essay_conversations")
    .delete()
    .eq("essay_id", essayId);

  if (convError) {
    console.error("❌ [API] Error deleting conversations:", convError);
    throw convError;
  }

  // Delete conversation metadata (summary, message count, etc.)
  const { error: metaError } = await supabase
    .from("essay_conversation_metadata")
    .delete()
    .eq("essay_id", essayId);

  if (metaError) {
    console.error("❌ [API] Error deleting conversation metadata:", metaError);
    throw metaError;
  }

  console.log("✅ [API] Successfully cleared all conversations and metadata");
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get the conversation summary for an essay, or empty string if none exists
 */
export async function getConversationSummary(essayId: string): Promise<string> {
  const metadata = await getEssayConversationMetadata(essayId);
  return metadata?.conversation_summary || "";
}

/**
 * Get recent conversation history for context (last N conversations)
 */
export async function getRecentConversationHistory(
  essayId: string,
  limit = 5
): Promise<EssayConversationDB[]> {
  const { data, error } = await supabase
    .from("essay_conversations")
    .select("*")
    .eq("essay_id", essayId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching recent conversation history:", error);
    throw error;
  }

  // Return in ascending order (oldest first)
  return (data || []).reverse();
}

/**
 * Get the latest quality scores for an essay from the most recent full_review feedback
 * Used by the dashboard to display quality breakdown
 * 
 * @param essayId - The essay UUID
 * @returns Quality scores and keywords, or null if no feedback exists
 */
export interface EssayQualityData {
  structure_score: number;
  voice_score: number;
  specificity_score: number;
  feedback_keywords: string[];
}

export async function getLatestEssayQuality(
  essayId: string
): Promise<EssayQualityData | null> {
  const { data, error } = await supabase
    .from("essay_conversations")
    .select("structure_score, voice_score, specificity_score, feedback_keywords")
    .eq("essay_id", essayId)
    .eq("intent", "full_review")  // Changed from "FULL_ANALYSIS" to match saved value
    .not("structure_score", "is", null)  // Only get rows with quality data
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Error fetching essay quality:", error);
    return null;
  }

  if (!data || data.structure_score === null) {
    // No feedback with quality scores exists
    return null;
  }

  return {
    structure_score: data.structure_score,
    voice_score: data.voice_score,
    specificity_score: data.specificity_score,
    feedback_keywords: data.feedback_keywords || [],
  };
}
